import { Intent, NonIdealState, ProgressBar } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import LoadingFullScreen from "components/common/LoadingFullScreen";
import { IN_QUEUE_PATH } from "infrastructure/RouterApp";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { segmentTrackOpenScenarioPage } from "./analytics";
import { BackButton } from "./components/common/BackButton";
import { SectionHeader } from "./components/common/SectionHeader";
import ScenarioActions from "./components/Scenario/ScenarioActions";
import { ScenarioAssumptions } from "./components/Scenario/ScenarioAssumptions";
import { ScenarioHeader } from "./components/Scenario/ScenarioHeader";
import ScenarioValidityWarning from "./components/Scenario/ScenarioValidityWarning";
import SubmitScenarioRunButton from "./components/Scenario/SubmitScenarioRunButton";
import { ScenarioResults } from "./components/scenarios/ScenarioResults/ScenarioResults";
import { useCluster, useStageConfig } from "./contexts/ClusterContext";
import {
    useScenarioMetadata,
    useTrackedProject
} from "./contexts/ProjectDataContext";
import {
    ScenarioPageProvider,
    useCurrentScenarioMetadata
} from "./contexts/ScenarioPageContext";
import css from "./ScenarioPage.module.scss";
import { ScenarioStatus } from "./types/scenarioType";
import { formatRelativeDate } from "./utils/formatting";

// Mirrors the scenario_team_id tag_concurrency_limits entry in the Dagster+ prod deployment settings.
const TEAM_CONCURRENT_SCENARIO_RUN_LIMIT = 20;

export const ScenarioPage: React.FC = () => {
    const { projectId, scenarioId } = useParams();
    const cluster = useCluster();
    const stage = useStageConfig();
    const navigate = useNavigate();
    const project = useTrackedProject({
        projectId,
        phase: cluster.studyPhase
    });
    const scenario = useScenarioMetadata(projectId, scenarioId);

    useEffect(() => {
        if (scenarioId && projectId) {
            segmentTrackOpenScenarioPage({
                scenarioId,
                projectId,
                cluster
            });
        }
    }, [scenarioId]);

    useEffect(() => {
        // Don't redirect until we have all the necessary data loaded
        // This prevents premature redirects when URLs are pasted directly
        if (projectId === undefined) {
            navigate(`/${IN_QUEUE_PATH}`);
            return;
        }

        // Wait for both project and scenario to finish loading before deciding to redirect
        if (project === "loading" || scenario === "loading") {
            // Still loading, don't redirect yet
            return;
        }

        if (project === undefined) {
            // Project doesn't exist, redirect to portfolio
            navigate(`/${IN_QUEUE_PATH}`);
        } else if (scenario === undefined) {
            // Project exists but scenario doesn't, redirect to project page
            navigate(`/${IN_QUEUE_PATH}/${cluster.slug}/${projectId}`);
        }
    }, [cluster.slug, projectId, project, scenario, navigate]);

    if (
        project === undefined ||
        project === "loading" ||
        scenario === undefined ||
        scenario === "loading"
    ) {
        return <LoadingFullScreen />;
    }

    return (
        <ScenarioPageProvider project={project} scenario={scenario}>
            <BackButton
                text={`Back to Project ${projectId}`}
                to={`/${IN_QUEUE_PATH}/${cluster.slug}/${projectId}`}
            />
            <div>
                <ScenarioHeader actions={<ScenarioActions />} />
                <ScenarioValidityWarning msg={stage.scenarioWarningMsg} />
            </div>
            <ScenarioBody />
        </ScenarioPageProvider>
    );
};

const ScenarioBody: React.FC = () => {
    const scenario = useCurrentScenarioMetadata();
    const { status } = scenario;
    if (status === ScenarioStatus.RunSucceeded) {
        return <ScenarioResults />;
    }

    let bodyElement = <></>;
    if (status === ScenarioStatus.Draft) {
        bodyElement = (
            <NonIdealState
                className={css["ScenarioPage-status"]}
                title="Draft scenario"
                icon={IconNames.EDIT}
                description={<div>Submit scenario to compute results.</div>}
                action={
                    <SubmitScenarioRunButton scenarioId={scenario.scenarioId} />
                }
            />
        );
    } else if (status === ScenarioStatus.RunFailed) {
        const description = (
            <div>
                Calculating results for this scenario failed unexpectedly. Nira
                has been notified of this issue and we're looking into it.
                <br />
                <br />
                To get in touch, please reach out to{" "}
                <a href="mailto:support@niraenergy.com">
                    support@niraenergy.com
                </a>
                .
            </div>
        );
        bodyElement = (
            <NonIdealState
                className={css["ScenarioPage-status"]}
                icon={IconNames.WARNING_SIGN}
                title="Scenario run failed"
                description={description}
            />
        );
    } else if (
        status === ScenarioStatus.RunRequested ||
        status === ScenarioStatus.RunSubmitted
    ) {
        const description = (
            <div className={css["running-description"]}>
                <span>
                    This scenario is waiting to start. Each team can run up to{" "}
                    {TEAM_CONCURRENT_SCENARIO_RUN_LIMIT} scenarios at a time,
                    and queued scenarios start in the order they were submitted.
                </span>
                <ProgressBar intent={Intent.NONE} />
            </div>
        );
        bodyElement = (
            <NonIdealState
                className={css["ScenarioPage-status"]}
                title="Scenario is queued..."
                description={description}
            />
        );
    } else if (status === ScenarioStatus.Running) {
        const runStartedAt = scenario.timeMetadata.runStartedAt;
        const description = (
            <div className={css["running-description"]}>
                <span>
                    Currently calculating results for this scenario.
                    {runStartedAt !== undefined && (
                        <>
                            <br />
                            <br />
                            Run started {formatRelativeDate(runStartedAt)} and
                            typically takes under 4 hours.
                        </>
                    )}
                </span>
                <ProgressBar value={0.4} intent={Intent.PRIMARY} />
            </div>
        );
        bodyElement = (
            <NonIdealState
                className={css["ScenarioPage-status"]}
                title="Scenario is running..."
                description={description}
            />
        );
    }

    return (
        <>
            <ScenarioAssumptions scenario={scenario} renderInPanel />
            <SectionHeader level="h2" title="Results" />
            {bodyElement}
        </>
    );
};
